import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductImage as ProductImageType } from '@alkor/types';
import { ProductImage } from './ui';
import { haptic } from '@/lib/telegram';

/**
 * Галерея товара со свайпом.
 *
 * Прокрутка нативная (scroll-snap), а не самописная: на телефоне это даёт
 * привычную инерцию и не конфликтует с жестом «назад» в Telegram.
 * Точки синхронизируются по позиции прокрутки, а не по счётчику кликов —
 * иначе они разъезжаются при быстром свайпе.
 */
export function Gallery({
  images, alt, artKind,
}: { images: ProductImageType[]; alt: string; artKind?: string | null }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const lastIndex = useRef(0);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    if (next !== lastIndex.current) {
      lastIndex.current = next;
      setIndex(next);
      haptic.select();
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const goTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  // Одно изображение или ни одного — трек не нужен, лишний скроллер только мешает
  if (images.length <= 1) {
    return (
      <ProductImage src={images[0]?.url} alt={images[0]?.alt ?? alt} artKind={artKind} opacity={0.7} eager />
    );
  }

  return (
    <>
      <div className="gallery__track" ref={trackRef} role="group" aria-label="Фотографии товара">
        {images.map((img, i) => (
          <div className="gallery__slide" key={img.url + i}>
            <ProductImage
              src={img.url}
              alt={img.alt ?? `${alt} — фото ${i + 1}`}
              artKind={artKind}
              opacity={0.7}
              // Первый кадр грузим сразу, соседний — заранее, остальные лениво
              eager={i <= index + 1}
            />
          </div>
        ))}
      </div>

      <div className="dots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={i === index ? 'on' : undefined}
            aria-label={`Фото ${i + 1} из ${images.length}`}
            aria-current={i === index}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </>
  );
}
